import { Share } from "@capacitor/share";
import { isPlatform } from "@ionic/react";
import { encodeParam, getPreBootstrapConfig } from "./ApiHelper";

// Resolves the public web host for shared links on native (FR-5), where the
// window origin is capacitor://localhost and not shareable. No host is
// hardcoded (a stale/wrong host would make shared links open in a browser
// instead of the app, or point at a dead domain). Resolution order:
//   1. preBootstrapConfig.MOBILE_WEB_HOST — optional explicit override.
//   2. the origin of preBootstrapConfig.INVOCATION_URL — this is the mobile web
//      host in every deployed environment, so no per-env value is normally
//      needed. Whatever host the shared link uses must be one the app is
//      associated with (iOS entitlements + Android intent-filter + served
//      AASA/assetlinks) for the deep link to open the app.
const getConfiguredNativeHost = async (): Promise<string | undefined> => {
  try {
    const config = await getPreBootstrapConfig();
    const explicit = config?.MOBILE_WEB_HOST;
    if (explicit && /^https?:\/\//i.test(explicit)) {
      return String(explicit).replace(/\/+$/, "");
    }
    const invocationUrl = config?.INVOCATION_URL;
    if (invocationUrl && /^https?:\/\//i.test(invocationUrl)) {
      return new URL(invocationUrl).origin;
    }
  } catch {
    // fall through
  }
  return undefined;
};

/**
 * Base URL for a shared work-order link (FR-5): current origin on web/PWA, the
 * configured/derived public host on native. Falls back to the current origin as
 * a last resort (should not happen in a deployed native build, which always has
 * an INVOCATION_URL).
 */
const getShareBaseUrl = async (): Promise<string> => {
  const currentOrigin =
    typeof window !== "undefined" && /^https?:/i.test(window.location?.origin || "")
      ? window.location.origin.replace(/\/+$/, "")
      : "";

  // Web / PWA: the current origin is the shareable host.
  if (!isPlatform("hybrid") && currentOrigin) return currentOrigin;

  // Native: derive from config (no usable window origin).
  const nativeHost = await getConfiguredNativeHost();
  return nativeHost ?? currentOrigin;
};

/**
 * Deep link to a work order's Details screen (FR-4):
 * {host}/workorder/{workOrderId}/details
 */
export const buildWorkOrderUrl = async (woNumber: string): Promise<string> => {
  const path = `/workorder/${encodeParam(woNumber)}/details`;
  return `${await getShareBaseUrl()}${path}`;
};

export interface ShareWorkOrderInput {
  woNumber: string;
  comment?: string;
  labels: {
    title: string;
    dialogTitle: string;
  };
}

export type ShareResult = "shared" | "copied";

/** Shared/copied payload body: optional comment followed by the deep link. */
export const buildWorkOrderShareText = async (
  input: ShareWorkOrderInput
): Promise<string> => {
  const url = await buildWorkOrderUrl(input.woNumber);
  const comment = input.comment?.trim();
  return comment ? `${comment}\n\n${url}` : url;
};

/**
 * Copies the work-order share message to the clipboard. Uses the async
 * Clipboard API, falling back to a hidden textarea + execCommand for older
 * webviews. Must be called from a user gesture. Returns true on success.
 */
export const copyWorkOrderToClipboard = async (
  input: ShareWorkOrderInput
): Promise<boolean> => {
  const text = await buildWorkOrderShareText(input);
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy path
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

/**
 * Shares a work-order deep link (plus optional comment) via the native share
 * sheet / Web Share API. Where no share sheet exists (most desktop browsers),
 * the link is copied to the clipboard instead (FR-6) and "copied" is returned
 * so the caller can show a confirmation toast. Cancelling the native sheet does
 * NOT copy (AC-6). Must be called from a user gesture.
 */
export const shareWorkOrder = async (
  input: ShareWorkOrderInput
): Promise<ShareResult> => {
  const text = await buildWorkOrderShareText(input);
  const title = input.labels.title;

  const canShare = await Share.canShare().catch(() => ({ value: false }));
  if (canShare?.value) {
    try {
      // Put the comment + link together in `text` and deliberately DO NOT pass a
      // separate `url`. Two reasons:
      //   1. Clickable — chat apps (WhatsApp, etc.) only auto-linkify URLs that
      //      are in the message text, so the link must live in `text` to be
      //      tappable by the recipient.
      //   2. No duplication — a separate `url` gets appended to `text` by some
      //      targets; since the link is already in `text`, that would show it
      //      twice. Omitting `url` keeps it to exactly one clickable link.
      // Trade-off: without a `url` field the OS share sheet can't render a rich
      // link-preview card and shows fewer link-specific targets (a Windows-
      // desktop nicety only). A single, clickable link for the recipient is the
      // priority.
      await Share.share({
        title,
        text,
        dialogTitle: input.labels.dialogTitle,
      });
    } catch {
      // User cancelled the share sheet — do NOT copy (AC-6).
    }
    return "shared";
  }

  // No share sheet (e.g. desktop browser) — copy the link instead (FR-6).
  await copyWorkOrderToClipboard(input);
  return "copied";
};
